import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl';
import { elevationPolygonsToPaint } from "utilities/mapbox_style_adapters";
import unitConversion from "utilities/unit_conversion";
import { isNil } from 'lodash';
import Color from 'color';

export default class ElevationPolygonSet {
  DEFAULT_PAINT = {
    'fill-extrusion-height': {
      'property': 'height',
      'type': 'identity'
    },
    'fill-extrusion-color': 'yellow',
    'fill-extrusion-opacity': 0.7
  };
  WIDTH_RADIUS = 20 / 1000;
  HEIGHT_MIN = 5;
  ZOOM_COEFFICIENTS = {
    1: 60,
    2: 50,
    3: 40,
    4: 30,
    5: 25,
    6: 20,
    7: 10,
    8: 5,
    9: 2
  };

  // @param [Object] elevationFeature
  //   The properties keys is expected to hold `elevationLow` and
  //   `elevationPeak` keys to facilitate the calculation of elevation
  //   polygons. The `elevationData` property should be an array of arrays
  //   holding data in the following format:
  //   [longitude, latitude, elevation, accumlated distance, gradient]
  //
  // @param options
  //   [mapboxgl.Map] map
  //   [Boolean] metric (default: true)
  //
  constructor(elevationFeatureOrFunction, options) {
    this._elevationFeatureOrFunction = elevationFeatureOrFunction;

    this.map = options.map;
    if (!this.map) throw "Must pass 'map' option to ElevationPolygonSet";

    this.metric = options.metric === undefined ? true : options.metric;

    this.style = options.style || {};

    this.metersPerPixel = this._calculateMetersPerPixel(
      options.bounds || options.map.getBounds()
    );

    this._setPopupFunctionality();

    this.activated = false;

    this.zoom = options.zoom;
  }

  set style(value) {
    this.paintProperties = {
      ...this.DEFAULT_PAINT,
      ...(elevationPolygonsToPaint(value).paint)
    };

    const color = Color(this.paintProperties['fill-extrusion-color']);
    this.paintProperties['fill-extrusion-color'] = [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      color.darken(0.4).hex(),
      this.paintProperties['fill-extrusion-color']
    ];

    if (this.geojson) {
      this.map.addSource('elevation-polygon', {
        'type': 'geojson',
        'data': this.geojson
      });
    }
  }

  setZoom(zoom) {
    this.zoom = zoom;
  }

  async _resolvePolygons() {
    if (this.geojson) return;

    if (!this.zoom) this.zoom = Math.floor(this.map.getZoom());

    let elevationFeature;
    if (this._elevationFeatureOrFunction instanceof Function) {
      elevationFeature = await this._elevationFeatureOrFunction();
    } else {
      elevationFeature = this._elevationFeatureOrFunction;
    }

    this.elevationMin = elevationFeature.properties.elevationLow;
    this.elevationMax = elevationFeature.properties.elevationPeak;
    if (isNil(this.elevationMin) || isNil(this.elevationMax)) {
      throw "No elevation max and/or min properties";
    }

    this.screenHeightConstant = this._calculateScreenHeightConstant(
      this.map.getContainer().offsetHeight
    );

    this.geojson = this._convertToPolygonGeoJSON(
      elevationFeature.properties.elevationData
    );

    this.map.addSource('elevation-polygon', {
      'type': 'geojson',
      'data': this.geojson
    });
  }

  hide(options = {}) {
    const mapChangeMethod = options.immediate ? "jumpTo" : "easeTo";

    this.map[mapChangeMethod]({
      pitch: 0,
      duration: 1000
    });

    if (this.map.getLayer('elevation-polygon-layer'))
      this.map.removeLayer('elevation-polygon-layer');

    this.activated = false;
  }

  remove() {
    this.hide();
    if (this.map.getSource('elevation-polygon'))
      this.map.removeSource('elevation-polygon');
  }

  isActivated() {
    return this.activated;
  }

  async show() {
    await this._resolvePolygons();

    this.map.easeTo({
      pitch: 40,
      duration: 1000
    });

    this.map.addLayer({
      'id': 'elevation-polygon-layer',
      'source': 'elevation-polygon',
      'type': 'fill-extrusion',
      'minzoom': 4,
      'paint': this.paintProperties
    });

    this.activated = true;
  }

  toggle() {
    if (this.activated)
      this.hide();
    else
      this.show();
  }

  _polygonWidth() {
    return this.WIDTH_RADIUS * (this.ZOOM_COEFFICIENTS[this.zoom] || 1);
  }

  _setPopupFunctionality() {
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'left',
      offset: 5,
      className: "elevation-popup"
    });

    this.map.on('mousemove', 'elevation-polygon-layer', (e) => {
      if (!e.features.length) {
        popup.remove();
        this.map.getCanvas().style.cursor = '';
        return;
      }

      // Set popup to track mouse
      this.map.getCanvas().style.cursor = 'pointer';
      popup.setLngLat(e.lngLat)
        .setHTML(this._popupHTML(e.features[0].properties))
        .addTo(this.map);

      if (this.hoveredPolygonId) {
        this.map.setFeatureState({
          source: 'elevation-polygon',
          id: this.hoveredPolygonId
        }, {
          hover: false
        });
      }
      this.hoveredPolygonId = e.features[0].id;
      this.map.setFeatureState({
        source: 'elevation-polygon',
        id: this.hoveredPolygonId
      }, {
        hover: true
      });
    });

    this.map.on('mouseleave', 'elevation-polygon-layer', () => {
      if (this.hoveredPolygonId) {
        try {
          this.map.setFeatureState({
            source: 'elevation-polygon',
            id: this.hoveredPolygonId
          }, {
            hover: false
          });
        } catch(e) {
          console.log("Captured error on mouse exiting elevation polygon", e);
        }
      }
      this.hoveredPolygonId = null;
      popup.remove();
      this.map.getCanvas().style.cursor = '';
    });
  }

  _convertToPolygonGeoJSON(elevationPoints) {
    const geojson = turf.featureCollection([]);
    const distanceIndicativeThatNextPointNotConnected = 0.8; // km
    // Elevation points do not necessary track the linestring; duplicates are
    // often pruned by the backend. This method helps mitigate effects this
    // has on the display of the polygons.
    const resolveAdjacentPoint = (pointCoords, adjacentCoords) => {
      if (!adjacentCoords) return null;

      const adjacentPoint = turf.point(adjacentCoords.slice(0, 2));
      const point = turf.point(pointCoords.slice(0, 2));
      const distanceToPoint = turf.distance(adjacentPoint, point);

      if (distanceToPoint > distanceIndicativeThatNextPointNotConnected) {
        return null;
      }

      return adjacentCoords;
    };

    elevationPoints.forEach((elevationPoint, index) => {
      const previousPoint = resolveAdjacentPoint(elevationPoint, elevationPoints[index - 1]);
      const subsequentPoint = resolveAdjacentPoint(elevationPoint, elevationPoints[index + 1]);
      const polygon = this._resolvePolygonFrom(
        previousPoint,
        elevationPoint,
        subsequentPoint
      );

      if (!polygon) return; // Could not be resolved because of distance between points

      polygon.id = index; // Mapbox GL only accepts an integer
      geojson.features.push(polygon);
    });

    return geojson;
  }

  _resolvePolygonFrom(previousCoords, coords, nextCoords) {
    if (!previousCoords && !nextCoords) {
      return null;
    } else if (!previousCoords) {
      return this._resolveHalfRectangleFrom(coords, nextCoords);
    } else if (!nextCoords) {
      return this._resolveHalfRectangleFrom(coords, previousCoords);
    }

    const vertices = [];

    const pointA = turf.point(previousCoords.slice(0, 2));
    const pointB = turf.point(coords.slice(0, 2));
    const pointC = turf.point(nextCoords.slice(0, 2));
    const distanceAB = turf.distance(pointA, pointB);
    const distanceCB = turf.distance(pointC, pointB);
    const bearingAB = turf.bearing(pointA, pointB);
    const bearingCB = turf.bearing(pointC, pointB);

    const pointAB1 = turf.destination(pointA, this._polygonWidth(), bearingAB - 90);
    const pointAB2 = turf.destination(pointAB1, distanceAB + (2 * this._polygonWidth()), bearingAB);
    const line1 = turf.lineString([turf.getCoord(pointAB1), turf.getCoord(pointAB2)]);

    const pointCB1 = turf.destination(pointC, this._polygonWidth(), bearingCB + 90);
    const pointCB2 = turf.destination(pointCB1, distanceCB + (2 * this._polygonWidth()), bearingCB);
    const line2 = turf.lineString([turf.getCoord(pointCB1), turf.getCoord(pointCB2)]);

    const pivotVertex1 = turf.lineIntersect(line1, line2).features[0];

    if (!pivotVertex1) {
      // There is no intersection because A, B, & C are in a straight line.
      return this._resolveFullRectangleFrom(previousCoords, coords, nextCoords);
    }

    const pivotVertex2 = turf.destination(
      pivotVertex1,
      this._polygonWidth() * 2,
      turf.bearing(pivotVertex1, pointB)
    );

    vertices.push(
      turf.destination(pointAB1, turf.distance(pointAB1, pivotVertex1) / 2, bearingAB)
    );
    vertices.push(pivotVertex1);
    vertices.push(
      turf.destination(pivotVertex1, turf.distance(pivotVertex1, pointCB1) / 2, bearingCB + 180)
    );
    vertices.push(
      turf.destination(vertices[2], this._polygonWidth() * 2, bearingCB - 90)
    );
    vertices.push(pivotVertex2);
    vertices.push(
      turf.destination(vertices[0], this._polygonWidth() * 2, bearingAB + 90)
    );
    vertices.push(vertices[0]);

    return turf.polygon(
      [vertices.map(pointFeature => turf.getCoord(pointFeature))],
      this._calculatePropertiesFrom(coords)
    );
  }

  _resolveHalfRectangleFrom(a, b) {
    const pointA = turf.point(a.slice(0, 2));
    const pointB = turf.point(b.slice(0, 2));
    const distance = turf.distance(pointA, pointB);
    const bearing = turf.bearing(pointA, pointB);
    let vertices = [];

    vertices.push(turf.destination(pointA, this._polygonWidth(), bearing - 90));
    vertices.push(turf.destination(vertices[0], distance / 2, bearing));
    vertices.push(turf.destination(vertices[1], this._polygonWidth() * 2, bearing + 90));
    vertices.push(turf.destination(vertices[2], distance / 2, bearing + 180));
    vertices.push(vertices[0]);

    return turf.polygon(
      [vertices.map(pointFeature => turf.getCoord(pointFeature))],
      this._calculatePropertiesFrom(a)
    );
  }

  _resolveFullRectangleFrom(a, b, c) {
    const pointA = turf.point(a.slice(0, 2));
    const pointB = turf.point(b.slice(0, 2));
    const pointC = turf.point(c.slice(0, 2));
    const distanceAB = turf.distance(pointA, pointB);
    const distanceBC = turf.distance(pointB, pointC);
    const bearingAB = turf.bearing(pointA, pointB);
    const bearingBC = turf.bearing(pointB, pointC);
    let vertices = [];

    vertices.push(turf.destination(pointB, this._polygonWidth(), bearingBC - 90));
    vertices.push(turf.destination(vertices[0], distanceBC / 2, bearingBC));
    vertices.push(turf.destination(vertices[1], this._polygonWidth() * 2, bearingBC + 90));
    vertices.push(
      turf.destination(vertices[2], (distanceBC / 2) + (distanceAB / 2), bearingBC + 180)
    );
    vertices.push(turf.destination(vertices[3], this._polygonWidth() * 2, bearingAB - 90));
    vertices.push(vertices[0]);

    return turf.polygon(
      [vertices.map(pointFeature => turf.getCoord(pointFeature))],
      this._calculatePropertiesFrom(b)
    );
  }

  _calculatePropertiesFrom(elevationPoint) {
    return {
      cumulativeDistance: elevationPoint[3],
      elevation: elevationPoint[2],
      gradient: elevationPoint[4],
      height: this._calculateHeightFrom(elevationPoint[2])
    };
  }

  _calculateHeightFrom(elevation) {
    const diff = (elevation - this.elevationMin);
    return this.screenHeightConstant * this.metersPerPixel * diff + this.HEIGHT_MIN;
  }

  // Return a constant for purposes of calculating the height of
  // an elevation column.  Large differentials approach e (2.718), low
  // approach 0.
  //
  _calculateScreenHeightConstant(screenHeight) {
    if (!screenHeight) throw "Invalid screenHeight provided";
    if (this.elevationMax === undefined || this.elevationMin == undefined) {
      throw "Screen height constant calculation requires max and min";
    }
    if (this.elevationMax == this.elevationMin) return 0;

    const diff = this.elevationMax - this.elevationMin;
    const a = 1;
    const b = Math.E;
    const c = 2.5;
    const d = 15;
    const adjusted = diff / 50;  // Good vars for display
    const x = adjusted < 1 ? b * adjusted : b;
    const y = d - (x - a) / (b - a) * (d - c);
    const result = (1 / diff) * (screenHeight / y);

    if (typeof result == "number") return result;

    throw "Could not calculate screen height constant";
  }

  _calculateMetersPerPixel(bounds) {
    const screenDistance = turf.distance(...bounds.toArray());
    const screenPixelLength = Math.sqrt(
      Math.pow(this.map.getContainer().offsetHeight, 2) +
      Math.pow(this.map.getContainer().offsetWidth, 2)
    );

    return (screenDistance * 1000) / screenPixelLength;
  }

  _popupHTML(properties) {
    const units = this.metric ? 'm' : 'ft';
    const elevation = Math.floor(
      unitConversion(properties.elevation, { from: 'm', to: units })
    );
    const gradient = Math.abs(Math.floor(properties.gradient));

    return `
      <div>
        <b>${elevation.toLocaleString()} ${units}</b>
        <span>(${gradient}%)</span>
      </div>
    `;
  }
}
